import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, take, concatMap, withLatestFrom } from 'rxjs/operators';
import { AuthFactoryService } from 'src/app/auth/authFactory.service';
import { APIService } from 'src/app/core/api/general.service';
import { LOCALSTORAGEVARS, OptionType } from 'src/app/core/enums/enums';
import { QuoteRequest, Location } from 'src/app/core/models/itinerary.model';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { ItineraryService } from '../../search-result/services/itinerary.service';

@Injectable()
export class VoluntaryChangeGuard implements CanActivate {
  quoteRequest: QuoteRequest;

  constructor(
    private api: APIService,
    private itineraryService: ItineraryService,
    private layoutService: LayoutService,
    private service: VoluntaryChangeService,
    private authFactoryService: AuthFactoryService,
    private settingsService: SettingsService
  ) {}

  loadVoluntaryChange(quoteRequest: QuoteRequest) {
    this.settingsService.dispatchGetClientSettings(this.authFactoryService.getUser().agencyId);
    return this.api.getVoluntaryQuote(quoteRequest).pipe(
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.settingsService.getClientSettings(),
            this.settingsService.getClientIsAirline()
          )
        )
      ),
      delay(0),
      map(([response, settings, isAirline]) => {
        if (JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true) {
          this.service.dispatchCameFromManualFlow();
        }
        this.layoutService.dispatchChangeBreadcrumb(quoteRequest.pnr, OptionType.VoluntaryChange);
        response.exchangeSegments = response.exchangeSegments.map((flight) => {
          flight.departure = this.addLabelToLocation(flight.departure);
          flight.arrival = this.addLabelToLocation(flight.arrival);
          return flight;
        });
        this.service.dispatchLoadVoluntaryQuoteSuccess(
          response,
          quoteRequest,
          settings.reissue.isVoluntary,
          isAirline,
          false,
          JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true
        );
        return !!response;
      }),
      catchError((response) => {
        this.itineraryService.dispatchGenericError(response.error);
        return this.error();
      })
    );
  }

  addLabelToLocation(location: Location): Location {
    return {
      ...location,
      label: `${location.code ? location.code : ''}  ${
        location.name ? ' - ' + location.name : ''
      }  ${location.airportName ? ' - ' + location.airportName : ''}`
    };
  }

  error() {
    this.itineraryService.navigateToWithParams('/itinerary');
    return of(false);
  }

  canActivate(): Observable<boolean> {
    this.itineraryService
      .getQuoteRequest()
      .pipe(take(1))
      .subscribe((response) => {
        return !!response ? (this.quoteRequest = response) : this.error();
      });
    return this.loadVoluntaryChange(this.quoteRequest);
  }
}
